import React, { useState, useMemo } from 'react'; // Import useMemo
import {
    Sidebar,
    Search, // Import Search component
    ConversationList,
    Conversation,
    Avatar
} from '@chatscope/chat-ui-kit-react';
import { UserCircleIcon, BuildingOffice2Icon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { UserPlusIcon, PlusIcon } from '@heroicons/react/24/outline'; // Add PlusIcon and outline version for button
import { Link } from 'react-router-dom'; // Import Link
import { Chat, PLACEHOLDER_AVATAR_SVG } from '../types';

// Define EnrichedChat interface matching the one in WhatsAppPage
interface EnrichedChat extends Chat {
    clientId?: string;
    isKnownClient?: boolean;
    contactName?: string; // Added contact person field    
    clientNames?: string[]; // Store all associated client names
    phone?: string;
}

interface ChatSidebarProps {
    chats: EnrichedChat[]; // Use enriched type
    activeChatId: string | null;
    onConversationClick: (chat: EnrichedChat) => void; // Use enriched type
    onAddChatClick: () => void; // Add prop for handling add chat click
    isCollapsed: boolean; // Prop to control sidebar collapse state
    toggleSidebar: () => void; // Function to toggle sidebar collapse state
}

// Helper function to normalize phone number to E.164 format (basic KZ example)
const normalizeToE164 = (phone: string): string => {
    // Remove all non-digit characters except leading '+'
    let cleaned = phone.replace(/[^\d+]/g, '');

    // Handle Kazakhstan numbers (starting with 8 or 7)
    if (cleaned.startsWith('8') && cleaned.length === 11) {
        // Replace leading 8 with +7
        return `+7${cleaned.substring(1)}`;
    } else if (cleaned.startsWith('7') && cleaned.length === 11) {
        // Prepend + if it starts with 7 and has 10 digits
        return `+${cleaned}`;
    } else if (cleaned.startsWith('+7') && cleaned.length === 12) {
        // Already looks like E.164 for KZ
        return cleaned;
    }

    // Fallback: return the cleaned number or original if cleaning failed
    return cleaned || phone;
};


const ChatSidebar: React.FC<ChatSidebarProps> = ({ chats, activeChatId, onConversationClick, onAddChatClick, isCollapsed, toggleSidebar }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredChats = useMemo(() => {
        if (!searchTerm) {
            return chats;
        }
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return chats.filter(chat => {
            const contactNameMatch = chat.contactName?.toLowerCase().includes(lowerCaseSearchTerm);
            const nameMatch = chat.name?.toLowerCase().includes(lowerCaseSearchTerm);
            const clientNamesMatch = chat.clientNames?.some(name => name?.toLowerCase().includes(lowerCaseSearchTerm));
            const phoneMatch = chat.phone?.toLowerCase().includes(lowerCaseSearchTerm); // Assuming phone is available
            return contactNameMatch || nameMatch || clientNamesMatch || phoneMatch;
        });
    }, [chats, searchTerm]);

    return (
        <div className="relative h-full flex-shrink-0">
            <Sidebar
                position="left"
                scrollable={false} // Disable direct sidebar scrolling
                style={{ flexBasis: isCollapsed ? '50px' : '300px', transition: 'flex-basis 0.3s ease' }} // Dynamic width and transition
                className="border-r border-gray-300 h-full max-h-dvh w-full lg:max-w-1/3 flex flex-col" // Add flex flex-col for vertical layout
            >
                {!isCollapsed && (
                    <> {/* Use fragment instead of div */}
                        <div className="flex items-center mx-2 my-1 flex-shrink-0"> {/* Flex container for Search and Add button - Make it non-shrinkable */}
                            <Search
                                placeholder="Поиск чатов..."
                                value={searchTerm}
                                onChange={setSearchTerm}
                                onClearClick={() => setSearchTerm('')}
                                className="flex-grow !rounded-none" // Allow search to grow
                            />
                            <button
                                onClick={onAddChatClick} // Use the passed handler
                                className="ml-2 p-2 rounded bg-gray-300 hover:bg-gray-400 dark:hover:bg-gray-700"
                                title="Добавить чат по номеру"
                            >
                                <PlusIcon className="h-5 w-5 text-gray-600 dark:text-gray-500" />
                            </button>
                        </div>
                        {/* Wrapper div for scrollable ConversationList */}
                        <div className="flex-grow overflow-y-auto">
                            <ConversationList>
                                {filteredChats.length === 0 && (
                                    <div className="p-4 text-center text-gray-500">
                                    {searchTerm ? 'Чаты не найдены' : 'Загрузка чатов...'}
                                </div>
                            )}
                            {filteredChats.map((chat) => {
                                // Normalize phone number before creating the link
                                const normalizedPhone = chat.phone ? normalizeToE164(chat.phone) : '';

                                return (
                                <Conversation
                                key={chat.id}
                                name={
                                    <div className="flex items-center justify-between w-full"> {/* Add flex container */}
                                        <div> {/* Container for name and client info */}
                                            <div className="flex items-center text-xs sm:text-sm">
                                                {chat.isKnownClient && (
                                                    <UserCircleIcon
                                                        className="h-4 w-4 text-blue-600 flex-shrink-0 mr-1" // Add margin
                                                        title="Известный клиент"
                                                    />
                                                )}
                                                {chat.contactName || chat.name}
                                            </div>
                                            {chat.clientNames?.map((name, index) => name && (
                                                <div key={index} className="text-[0.625rem] sm:text-xs text-gray-500 break-words whitespace-normal">
                                                    <BuildingOffice2Icon
                                                        className="h-3 w-3 inline-block mr-1 mb-1 text-gray-400"
                                                        title={name}
                                                    />{name}
                                                </div>
                                            ))}
                                        </div>
                                        {!chat.isKnownClient && normalizedPhone && ( // Use normalizedPhone
                                            <Link
                                                to={`/contacts/new?phone=${encodeURIComponent(normalizedPhone)}`} // Use normalizedPhone
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="ml-2 p-1 rounded hover:bg-gray-200 flex-shrink-0" // Add styling
                                                title="Быстро добавить контакт"
                                                onClick={(e) => e.stopPropagation()} // Prevent conversation click
                                            >
                                                <UserPlusIcon className="h-4 w-4 text-gray-600" />
                                            </Link>
                                        )}
                                    </div>
                                }
                                info={chat.lastMessage ? (chat.lastMessage.length > 30 ? chat.lastMessage.substring(0, 27) + '...' : chat.lastMessage) : 'Нет сообщений'}
                                    // Add !important modifier to Tailwind classes and font size classes
                                    className={`items-center text-xs sm:text-sm ${chat.isKnownClient ? '!bg-green-50 hover:!bg-green-100' : ''}`}
                                    active={activeChatId === chat.id}
                                    unreadCnt={chat.unreadCount || 0}
                                    onClick={() => onConversationClick(chat)}
                                >
                                    <Avatar
                                        src={chat.profilePicUrl || PLACEHOLDER_AVATAR_SVG}
                                        name={chat.name}
                                        size="sm"
                                    // Optionally add status based on presence if available globally
                                    // status={activeChatId === chat.id ? "available" : undefined}
                                    />
                                </Conversation>
                                )})}
                            </ConversationList>
                        </div> {/* Close the scrollable wrapper div */}
                    </> // Close the fragment
                )}
            </Sidebar>
            {/* Toggle Button */}
            <button
                onClick={toggleSidebar}
                className="absolute top-1/2 -right-3 transform -translate-y-1/2 bg-white border border-gray-300 rounded-full p-1 shadow-md hover:bg-gray-100 z-10"
                title={isCollapsed ? "Развернуть" : "Свернуть"}
            >
                {isCollapsed ? (
                    <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                ) : (
                    <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
                )}
            </button>
        </div>
    );
};

export default ChatSidebar;
